import React from "react";
import {
  Box,
  Card,
  Grid,
  Skeleton,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  LinearProgress,
  Chip,
} from "@material-ui/core";
import {
  Assignment,
  Warning,
  CheckCircle,
  Cancel,
  Schedule,
} from "@material-ui/icons";
import { fNumber, fPercent } from "src/utils/formatNumber";
import AppDetalleClientes from "../estadisticas/app-detalle-clientes";

export default function ResumenTareasEmpleado({ data, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (loading) {
    return (
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} md={7}>
          <Skeleton variant="rectangular" height={isMobile ? 300 : 450} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Skeleton variant="rectangular" height={isMobile ? 300 : 450} />
        </Grid>
      </Grid>
    );
  }

  // Procesar datos
  const tareasMap =
    data?.tareasEstado?.reduce((acc, item) => {
      acc[item.estado] = parseInt(item.cantidad);
      return acc;
    }, {}) || {};

  const totalTareas =
    data?.tareasEstado?.reduce(
      (sum, item) => sum + parseInt(item.cantidad),
      0,
    ) || 0;

  const tareasCompletadas =
    (tareasMap["TERMINADA"] || 0) + (tareasMap["FACTURADO"] || 0);
  const porcentajeCompletadas =
    totalTareas > 0 ? (tareasCompletadas / totalTareas) * 100 : 0;

  const tareasActivas =
    (tareasMap["PENDIENTE"] || 0) +
    (tareasMap["EN PROCESO"] || 0) +
    (tareasMap["PENDIENTE DE FACTURACION"] || 0);

  const tareasCanceladas = tareasMap["CANCELADO O RECHAZADO"] || 0;
  const tareasProximasVencer = data?.tareasProximasVencer || 0;

  // Componente de métrica individual
  const MetricCard = ({ icon, label, value, color, subtitle }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={1.5}>
        <Box
          sx={{
            p: 1,
            bgcolor: `${color}.lighter`,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box flex={1}>
          <Typography variant="caption" color="textSecondary" display="block">
            {label}
          </Typography>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            fontWeight="bold"
            color={`${color}.main`}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Card sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
            Resumen de Tareas
          </Typography>
          <Chip
            icon={<Assignment />}
            label={`Total: ${fNumber(totalTareas)}`}
            color="primary"
            sx={{ fontWeight: "bold" }}
          />
        </Box>
        <Divider />
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Gráfico Donut */}
        <Grid item xs={12} lg={7}>
          <Box sx={{ height: "100%" }}>
            <AppDetalleClientes
              title="Distribución de Tareas"
              subheader="Por estado actual"
              total={totalTareas}
              loading={false}
              chart={{
                series: [
                  { label: "Pendientes", value: tareasMap["PENDIENTE"] || 0 },
                  { label: "En Proceso", value: tareasMap["EN PROCESO"] || 0 },
                  { label: "Terminadas", value: tareasMap["TERMINADA"] || 0 },
                  { label: "Facturadas", value: tareasMap["FACTURADO"] || 0 },
                  {
                    label: "Pend. Facturación",
                    value: tareasMap["PENDIENTE DE FACTURACION"] || 0,
                  },
                  {
                    label: "Canceladas",
                    value: tareasMap["CANCELADO O RECHAZADO"] || 0,
                  },
                ].filter((item) => item.value > 0),
                colors: [
                  ["#ffeb3b", "#ffc107"], // Pendientes - Amarillo
                  ["#2196f3", "#1976d2"], // En Proceso - Azul
                  ["#4caf50", "#388e3c"], // Terminadas - Verde
                  ["#9c27b0", "#7b1fa2"], // Facturadas - Morado
                  ["#ff9800", "#f57c00"], // Pend. Facturación - Naranja
                  ["#f44336", "#d32f2f"], // Canceladas - Rojo
                ],
              }}
            />
          </Box>
        </Grid>

        {/* Métricas Rápidas */}
        <Grid item xs={12} lg={5}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              color="textSecondary"
              fontWeight="medium"
            >
              Métricas Clave
            </Typography>

            {/* Tareas Activas */}
            <MetricCard
              icon={<Schedule sx={{ color: "info.main" }} />}
              label="Tareas Activas"
              value={fNumber(tareasActivas)}
              color="info"
              subtitle="En progreso"
            />

            {/* Próximas a Vencer */}
            <MetricCard
              icon={<Warning sx={{ color: "warning.main" }} />}
              label="Próximas a Vencer"
              value={fNumber(tareasProximasVencer)}
              color="warning"
              subtitle="Próximos 7 días"
            />

            {/* Progreso de Completadas */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <Box
                  sx={{
                    p: 1,
                    bgcolor: "success.lighter",
                    borderRadius: 1.5,
                    display: "flex",
                  }}
                >
                  <CheckCircle sx={{ color: "success.main" }} />
                </Box>
                <Box flex={1}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="textSecondary">
                      Completadas
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="success.main"
                    >
                      {fPercent(porcentajeCompletadas)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={porcentajeCompletadas}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: "success.main",
                    borderRadius: 4,
                  },
                }}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                mt={0.5}
              >
                {fNumber(tareasCompletadas)} de {fNumber(totalTareas)} tareas
              </Typography>
            </Paper>

            {/* Canceladas */}
            {tareasCanceladas > 0 && (
              <MetricCard
                icon={<Cancel sx={{ color: "error.main" }} />}
                label="Canceladas"
                value={fNumber(tareasCanceladas)}
                color="error"
              />
            )}

            {/* Resumen total - Solo desktop */}
            {!isMobile && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: "auto",
                  bgcolor: "primary.lighter",
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Total General
                </Typography>
                <Typography variant="h4" color="primary.main" fontWeight="bold">
                  {fNumber(totalTareas)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  tareas asignadas
                </Typography>
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
}
